import { CenteredLazyLoading } from "@/controls/LazyLoading";
import { dxtToLocalServerTime } from "@dexteel/mesf-core";
import { Box, Paper, Tooltip, Typography } from "@mui/material";
import type {
	CellStyle,
	ColDef,
	ColGroupDef,
	ExcelStyle,
	GridApi,
	ValueFormatterParams,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import {
	createMeterNameResolver,
	type UtilityConsumptionMeter,
} from "../models/UtilityConsumption";
import { NO_DATA_TEXT, RATIO_UNIT, VOLUME_UNIT } from "../utils/format";
import {
	createConsumptionReportTotalRow,
	formatConsumptionReportCost,
	formatConsumptionReportGallons,
	getConsumptionReportMeterCoverageField,
	getConsumptionReportMeterField,
	type ConsumptionReportGridRow,
} from "../utils/reportGrid";

const gallonsFormatter = ({ value }: { value: number | null | undefined }) =>
	formatConsumptionReportGallons(value);

const excelStyles: ExcelStyle[] = [
	{
		id: "partialCoverage",
		interior: { color: "#FFF4E0", pattern: "Solid" },
		font: { color: "#8A5200" },
	},
];

const PartialDot = () => (
	<Box
		component="span"
		sx={{
			bgcolor: "#B26A00",
			borderRadius: "50%",
			flexShrink: 0,
			height: 7,
			width: 7,
		}}
	/>
);

const MeterCell = ({
	value,
	coverage,
	period,
	meterId,
}: {
	value?: number | null;
	coverage?: number | null;
	period?: string;
	meterId: string;
}) => {
	const inPeriod = period ? ` in ${period}` : "";
	if (value === null || value === undefined) return <>{NO_DATA_TEXT}</>;

	const text = formatConsumptionReportGallons(value);
	if (coverage === null || coverage === undefined || coverage >= 100)
		return <>{text}</>;

	return (
		<Tooltip
			title={`${meterId} reported ${Math.round(coverage)}% of the hours${inPeriod}: this total is what it measured, not what went through it.`}
		>
			<Box
				component="span"
				sx={{ alignItems: "center", display: "inline-flex", gap: 0.5 }}
			>
				<PartialDot />
				{text}
			</Box>
		</Tooltip>
	);
};

const markPartial = (
	text: string,
	data: ConsumptionReportGridRow | undefined,
) => (data?.missingMeters ? `${text}*` : text);

const totalGallonsFormatter = ({
	value,
	data,
}: ValueFormatterParams<ConsumptionReportGridRow, number | null>) =>
	markPartial(formatConsumptionReportGallons(value), data);

const totalCostFormatter = ({
	value,
	data,
}: ValueFormatterParams<ConsumptionReportGridRow, number | null>) =>
	markPartial(
		formatConsumptionReportCost(value, data?.isCostApproximate),
		data,
	);

const compactHeaderStyle = { fontSize: "0.75rem" };
const ACCOUNT_GROUP_START_CLASS = "consumption-account-start";

const numericCellStyle = ({ value }: { value: number | null | undefined }) =>
	value === null || value === undefined
		? { fontVariantNumeric: "tabular-nums", color: "#A9B0B7" }
		: ({ fontVariantNumeric: "tabular-nums" } as CellStyle);

const MILL_BADGE_TINTS = [
	{ backgroundColor: "#DCE9F7", color: "#0A3D6B" },
	{ backgroundColor: "#EFEAF7", color: "#4A3B78" },
] as const;

const MillBadge = ({
	value,
	tints,
}: {
	value?: string;
	tints: Record<string, (typeof MILL_BADGE_TINTS)[number]>;
}) =>
	value ? (
		<Box
			component="span"
			sx={{
				...tints[value],
				borderRadius: 1,
				display: "inline-block",
				fontSize: "0.7rem",
				fontWeight: 600,
				lineHeight: 1.6,
				px: 0.75,
			}}
		>
			{value}
		</Box>
	) : null;

const getIdentityColumns = (
	periodHeader: string,
	isSection: boolean,
	showMill: boolean,
	millTints: Record<string, (typeof MILL_BADGE_TINTS)[number]>,
): ColDef<ConsumptionReportGridRow>[] =>
	isSection
		? [
				...(showMill
					? [
							{
								headerName: "Mill",
								field: "mill",
								flex: 1.1,
								cellRenderer: MillBadge,
								cellRendererParams: { tints: millTints },
								cellStyle: { display: "flex", alignItems: "center" },
							} satisfies ColDef<ConsumptionReportGridRow>,
						]
					: []),
				{
					headerName: "Event",
					field: "event",
					flex: 0.8,
				},
				{
					headerName: "Section",
					field: "section",
					flex: 0.8,
					headerClass: "ag-center-aligned-header",
					cellStyle: { textAlign: "center" },
				},
				{
					headerName: "Event Start",
					field: "eventStart",
					valueGetter: (params) =>
						dxtToLocalServerTime(
							(params.data as ConsumptionReportGridRow)?.eventStart,
							"MM/dd/yy HH:mm",
						) || "",
					flex: 1.4,
				},
				{
					headerName: "Event End",
					field: "eventEnd",
					valueGetter: (params) =>
						dxtToLocalServerTime(
							(params.data as ConsumptionReportGridRow)?.eventEnd,
							"MM/dd/yy HH:mm",
						) || "",
					flex: 1.4,
				},
			]
		: [
				{
					headerName: periodHeader,
					field: "period",
					flex: 1.5,
					cellStyle: { textAlign: "center" },
				},
			];

const formatSharePercent = (value: number) =>
	`${value.toLocaleString("en-US", { maximumFractionDigits: 2 })}%`;

const getMeterColumn = (
	meterId: string,
	index: number,
	meter: UtilityConsumptionMeter | undefined,
	getMeterName: (meterId: string) => string,
): ColDef<ConsumptionReportGridRow> => {
	const label = getMeterName(meterId);
	const sharePercent = meter?.SharePercent;

	return {
		colId: `meter:${meterId}`,
		headerName:
			sharePercent !== undefined && sharePercent > 0 && sharePercent < 100
				? `${label} (${formatSharePercent(sharePercent)})`
				: label,
		field: getConsumptionReportMeterField(index),
		flex: 1.3,
		minWidth: 110,
		type: "numericColumn",
		cellStyle: numericCellStyle,
		cellClass: (params: { data?: ConsumptionReportGridRow }) => {
			const value = params.data?.[getConsumptionReportMeterField(index)];
			const coverage = params.data?.[
				getConsumptionReportMeterCoverageField(index)
			] as number | null | undefined;
			return typeof value === "number" &&
				typeof coverage === "number" &&
				coverage < 100
				? "partialCoverage"
				: "";
		},
		cellRenderer: ({
			value,
			data,
		}: {
			value?: number | null;
			data?: ConsumptionReportGridRow;
		}) => (
			<MeterCell
				value={value}
				coverage={
					data?.[getConsumptionReportMeterCoverageField(index)] as
						| number
						| null
						| undefined
				}
				period={data?.period}
				meterId={meterId}
			/>
		),
		valueFormatter: gallonsFormatter,
		useValueFormatterForExport: true,
	};
};

interface MeterColumnGroup {
	key: string;
	name: string;
	tooltip?: string;
}
const getMeterGroups = (meters: UtilityConsumptionMeter[]) => {
	const groups: MeterColumnGroup[] = [];
	const groupKeyByMeterId: Record<string, string> = {};

	for (const meter of meters) {
		const key = `account:${meter.WaterAccountId ?? "none"}`;
		groupKeyByMeterId[meter.ExternalMeterId] = key;

		if (groups.some((group) => group.key === key)) continue;

		groups.push({
			key,
			name: meter.AccountNumber ?? "No account",
			tooltip: meter.AccountName ?? undefined,
		});
	}

	return { groups, groupKeyByMeterId };
};

const getMeterColumns = (
	meterIds: string[],
	meterById: Record<string, UtilityConsumptionMeter>,
	groups: MeterColumnGroup[],
	groupKeyByMeterId: Record<string, string>,
): (
	| ColDef<ConsumptionReportGridRow>
	| ColGroupDef<ConsumptionReportGridRow>
)[] => {
	const getMeterName = createMeterNameResolver(Object.values(meterById));
	const meterColumn = (meterId: string, index: number) =>
		getMeterColumn(meterId, index, meterById[meterId], getMeterName);

	if (groups.length <= 1) return meterIds.map(meterColumn);

	return groups
		.map((group) => ({
			group,
			columns: meterIds.flatMap((meterId, index) =>
				groupKeyByMeterId[meterId] === group.key
					? [meterColumn(meterId, index)]
					: [],
			),
		}))
		.filter(({ columns }) => columns.length > 0)
		.map(({ group, columns }, index) => {
			const [firstColumn, ...restColumns] = columns;
			return {
				headerName: group.name,
				headerTooltip: group.tooltip,
				suppressStickyLabel: true,
				headerClass: index === 0 ? undefined : ACCOUNT_GROUP_START_CLASS,
				children: [
					index === 0
						? firstColumn
						: {
								...firstColumn,
								headerClass: ACCOUNT_GROUP_START_CLASS,
							},
					...restColumns,
				],
			} satisfies ColGroupDef<ConsumptionReportGridRow>;
		});
};

const getColumnDefs = (
	meterIds: string[],
	meterById: Record<string, UtilityConsumptionMeter>,
	meterGroups: ReturnType<typeof getMeterGroups>,
	periodHeader: string,
	totalHeader: string,
	showCost: boolean,
	showRatio: boolean,
	isSection: boolean,
	showMill: boolean,
	millTints: Record<string, (typeof MILL_BADGE_TINTS)[number]>,
): (
	| ColDef<ConsumptionReportGridRow>
	| ColGroupDef<ConsumptionReportGridRow>
)[] => [
	...getIdentityColumns(periodHeader, isSection, showMill, millTints),
	{
		headerName: `Consumption by meter (${VOLUME_UNIT})`,
		suppressStickyLabel: true,
		children: getMeterColumns(
			meterIds,
			meterById,
			meterGroups.groups,
			meterGroups.groupKeyByMeterId,
		),
	},
	...(showRatio
		? [
				{
					headerName: RATIO_UNIT,
					headerTooltip: `Gallons per ton`,
					headerStyle: isSection ? compactHeaderStyle : undefined,
					field: "gallonsPerTon",
					flex: 1.4,
					type: "numericColumn",
					cellStyle: numericCellStyle,
					valueFormatter: totalGallonsFormatter,
					useValueFormatterForExport: true,
				} satisfies ColDef<ConsumptionReportGridRow>,
			]
		: []),
	...(showCost
		? [
				{
					headerName: "$ / ton",
					headerStyle: isSection ? compactHeaderStyle : undefined,
					field: "costPerTon",
					flex: 1.3,
					type: "numericColumn",
					cellStyle: numericCellStyle,
					valueFormatter: totalCostFormatter,
					useValueFormatterForExport: true,
				} satisfies ColDef<ConsumptionReportGridRow>,
			]
		: []),
	{
		headerName: `${totalHeader} (${VOLUME_UNIT})`,
		headerStyle: compactHeaderStyle,
		field: "gallons",
		flex: 1.3,
		type: "numericColumn",
		cellStyle: numericCellStyle,
		valueFormatter: totalGallonsFormatter,
		useValueFormatterForExport: true,
	},
	...(showCost
		? [
				{
					headerName: `${totalHeader} ($)`,
					headerTooltip:
						"Priced from the water bills of the meters' accounts. \u2248 means no bill covers that period yet, so the price of the previous bill is used.",
					headerStyle: compactHeaderStyle,
					field: "cost",
					flex: 1.3,
					type: "numericColumn",
					cellStyle: numericCellStyle,
					valueFormatter: totalCostFormatter,
					useValueFormatterForExport: true,
				} satisfies ColDef<ConsumptionReportGridRow>,
			]
		: []),
];

const defaultColDef: ColDef<ConsumptionReportGridRow> = {
	editable: false,
	filter: false,
	resizable: true,
	sortable: true,
	suppressHeaderMenuButton: true,
};

interface ConsumptionReportGridProps {
	rows: ConsumptionReportGridRow[];
	meterIds: string[];
	meters: UtilityConsumptionMeter[];
	meterTotalGallons: Record<string, number | null>;
	grandTotalGallons: number | null;
	grandGallonsPerTon: number | null;
	grandGallonsPerBillet: number | null;
	grandTotalCost: number | null;
	grandCostPerTon: number | null;
	isCostApproximate: boolean;
	periodHeader: string;
	totalHeader: string;
	showCost: boolean;
	showRatio?: boolean;
	isSection: boolean;
	showMill: boolean;
	isLoading: boolean;
	onGridReady: (api: GridApi<ConsumptionReportGridRow>) => void;
}

export const ConsumptionReportGrid = ({
	rows,
	meterIds,
	meters,
	meterTotalGallons,
	grandTotalGallons,
	grandGallonsPerTon,
	grandGallonsPerBillet,
	grandTotalCost,
	grandCostPerTon,
	isCostApproximate,
	periodHeader,
	totalHeader,
	showCost,
	showRatio = true,
	isSection,
	showMill,
	isLoading,
	onGridReady,
}: ConsumptionReportGridProps) => {
	const meterById = Object.fromEntries(
		meters.map((meter) => [meter.ExternalMeterId, meter]),
	);
	const meterGroups = getMeterGroups(meters);

	const millNames = [...new Set(meters.map((meter) => meter.MillName))];
	const millTints = Object.fromEntries(
		millNames.map((millName, index) => [
			millName,
			MILL_BADGE_TINTS[index % MILL_BADGE_TINTS.length],
		]),
	);

	const hasPartialTotals = rows.some((row) => row.missingMeters !== "");
	const hasPartialCells = rows.some((row) =>
		Object.entries(row).some(
			([field, value]) =>
				field.endsWith("_coverage") && typeof value === "number" && value < 100,
		),
	);

	const columnDefs = getColumnDefs(
		meterIds,
		meterById,
		meterGroups,
		periodHeader,
		totalHeader,
		showCost,
		showRatio,
		isSection,
		showMill,
		millTints,
	);

	return (
		<Paper
			component="section"
			elevation={2}
			sx={{
				display: "flex",
				flexDirection: "column",
				flex: { lg: 1 },
				height: { xs: 420, lg: "auto" },
				minHeight: { xs: 420, lg: 0 },
				p: 1,
				[`& .${ACCOUNT_GROUP_START_CLASS}`]: {
					boxShadow: "inset 1px 0 0 rgba(0, 0, 0, 0.18)",
				},
			}}
		>
			<Box sx={{ flex: 1, minHeight: 0 }}>
				<AgGridReact<ConsumptionReportGridRow>
					loading={isLoading}
					rowData={rows}
					pinnedBottomRowData={[
						createConsumptionReportTotalRow({
							meterIds,
							meterTotalGallons,
							grandTotalGallons,
							grandGallonsPerTon,
							grandGallonsPerBillet,
							grandTotalCost,
							grandCostPerTon,
							isCostApproximate,
							isSection,
						}),
					]}
					columnDefs={columnDefs}
					defaultColDef={defaultColDef}
					rowHeight={26}
					headerHeight={30}
					groupHeaderHeight={24}
					getRowId={(params) => params.data.id}
					getRowStyle={(params) =>
						params.node.rowPinned ? { fontWeight: 700 } : undefined
					}
					excelStyles={excelStyles}
					onGridReady={(params) => onGridReady(params.api)}
					loadingOverlayComponent={CenteredLazyLoading}
					suppressContextMenu
				/>
			</Box>
			{(hasPartialTotals || hasPartialCells) && (
				<Typography
					component="div"
					sx={{
						color: "#5B6670",
						fontSize: "0.7rem",
						lineHeight: 1.4,
						pt: 0.5,
						px: 0.5,
					}}
				>
					<Box
						component="span"
						sx={{
							alignItems: "center",
							display: "inline-flex",
							mr: 0.75,
							verticalAlign: "baseline",
						}}
					>
						<PartialDot />
					</Box>
					marks a figure the meter measured only part of the period for, and the
					totals left short of a meter are marked *. Hover either to see how
					much.
				</Typography>
			)}
		</Paper>
	);
};
